import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import {
  LayoutDashboard, BarChart3, ArrowRightLeft, BookOpen,
  MessageSquare, Users, Shield, FileText, Settings, Bell,
  LogOut, UserCircle, Trophy, Star, Inbox, TrendingUp, DollarSign,
  Calendar, X
} from 'lucide-react';


const Layout = ({ children }) => {
  const { user, logout, isAdmin, isBDE, isReadOnly, canSeeTeamData, canSeeStrategy, canAccessUpwork } = useAuth();
  const navigate = useNavigate();

  // Upwork referrals pending count for the sidebar badge.
  // Fetched on mount + whenever a referral is converted/closed.
  // UpworkReferralDetail dispatches 'upwork-referrals-changed' after the
  // local action's API call resolves, which is why a route-change-based
  // refetch alone isn't enough (Close keeps the user on the detail page).
  // External ingests via the public API won't refresh the badge until the
  // user reloads — acceptable for an "approximately recent" indicator.
  const [upworkPending, setUpworkPending] = useState(0);

  useEffect(() => {
    if (!canAccessUpwork) return;  // Users without the access flag skip the 403
    const fetchCounts = () => {
      api.get('/upwork-referrals/counts')
        .then(res => setUpworkPending(res.data.pending || 0))
        .catch(() => { /* non-fatal — leave badge as-is */ });
    };
    fetchCounts();
    window.addEventListener('upwork-referrals-changed', fetchCounts);
    return () => window.removeEventListener('upwork-referrals-changed', fetchCounts);
  }, [canAccessUpwork]);

  // Monthly-report nag popup (admin only). Polls the "current ready" report
  // on mount (i.e. every page load / full refresh). Cancel is a page-local
  // dismiss only — it never touches the report's status, so the popup
  // re-fires on the next load and every subsequent day until the admin
  // actually opens the report and clicks "Copy all" (which flips it to done
  // server-side). `dismissed` resets naturally because Layout remounts on a
  // full page load.
  const [reportPopup, setReportPopup] = useState(null);
  const [reportDismissed, setReportDismissed] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    api.get('/monthly-reports/current')
      .then((res) => {
        // 204 => nothing pending (no body); anything else carries the report.
        if (res.status === 204 || !res.data?.report) return;
        setReportPopup(res.data.report);
      })
      .catch(() => { /* non-fatal — no nag if the check fails */ });
  }, [isAdmin]);

  const monthLabel = (key) => {
    if (!key) return '';
    const [y, m] = key.split('-').map(Number);
    const name = new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long' });
    return `${name} ${y}`;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="app-layout">
      <aside className="sidebar">
        <div className="sidebar-logo">
          <h1>SoftRadix</h1>
          <p>LinkedIn Sales Tool</p>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">Main</div>

          {canSeeTeamData && (
            <NavLink to="/dashboard" className={({isActive}) => isActive ? 'active' : ''}>
              <LayoutDashboard size={18} /> Dashboard
            </NavLink>
          )}

          {isAdmin && (
            <NavLink to="/admin/ai-cost" className={({isActive}) => isActive ? 'active' : ''}>
              <DollarSign size={18} /> AI Cost
            </NavLink>
          )}

          <NavLink to="/leads" className={({isActive}) => isActive ? 'active' : ''}>
            <Users size={18} /> Leads
          </NavLink>

          {canSeeTeamData && (
            <NavLink to="/lead-performance" className={({isActive}) => isActive ? 'active' : ''}>
              <TrendingUp size={18} /> Lead Performance
            </NavLink>
          )}

          {isBDE && (
            <NavLink to="/my-stats" className={({isActive}) => isActive ? 'active' : ''}>
              <BarChart3 size={18} /> My Stats
            </NavLink>
          )}

          {!isReadOnly && (
            <NavLink to="/daily-log" className={({isActive}) => isActive ? 'active' : ''}>
              <BarChart3 size={18} /> Daily Log
            </NavLink>
          )}

          <NavLink to="/leaderboard" className={({isActive}) => isActive ? 'active' : ''}>
            <Trophy size={18} /> Leaderboard
          </NavLink>

          <NavLink to="/handoffs" className={({isActive}) => isActive ? 'active' : ''}>
            <ArrowRightLeft size={18} /> Handoffs
          </NavLink>

          {canAccessUpwork && (
            <NavLink to="/upwork-referrals" className={({isActive}) => isActive ? 'active' : ''}>
              <Inbox size={18} /> Upwork Referrals
              {upworkPending > 0 && (
                <span className="badge badge-amber" style={{ marginLeft: 8 }}>{upworkPending}</span>
              )}
            </NavLink>
          )}

          <NavLink to="/follow-ups" className={({isActive}) => isActive ? 'active' : ''}>
            <Bell size={18} /> Follow-ups
          </NavLink>

          <NavLink to="/best-messages" className={({isActive}) => isActive ? 'active' : ''}>
            <Star size={18} /> Best Message
          </NavLink>

          <div className="nav-section">Resources</div>

          <NavLink to="/guidelines" className={({isActive}) => isActive ? 'active' : ''}>
            <BookOpen size={18} /> Guidelines
          </NavLink>

          <NavLink to="/review-notes" className={({isActive}) => isActive ? 'active' : ''}>
            <MessageSquare size={18} /> Review Notes
          </NavLink>

          <NavLink to="/profiles" className={({isActive}) => isActive ? 'active' : ''}>
            <UserCircle size={18} /> Profile Assignments
          </NavLink>

          {canSeeStrategy && (
            <>
              <div className="nav-section">Strategy</div>
              <NavLink to="/gate2" className={({isActive}) => isActive ? 'active' : ''}>
                <Shield size={18} /> Gate 2
              </NavLink>
              <NavLink to="/playbook" className={({isActive}) => isActive ? 'active' : ''}>
                <FileText size={18} /> Playbook
              </NavLink>
            </>
          )}

          {isAdmin && (
            <>
              <div className="nav-section">Admin</div>
              <NavLink to="/users" className={({isActive}) => isActive ? 'active' : ''}>
                <Users size={18} /> User Management
              </NavLink>
              <NavLink to="/admin/monthly-report" className={({isActive}) => isActive ? 'active' : ''}>
                <Calendar size={18} /> Monthly Report
              </NavLink>
              <NavLink to="/settings" className={({isActive}) => isActive ? 'active' : ''}>
                <Settings size={18} /> Settings
              </NavLink>
            </>
          )}

          <NavLink to="/notifications" className={({isActive}) => isActive ? 'active' : ''}>
            <Bell size={18} /> Notifications
          </NavLink>
        </nav>

        <div className="sidebar-user">
          <div className="user-name">{user?.name}</div>
          <div className="user-role">{user?.role?.replace('_', ' ')}</div>
          <button onClick={handleLogout}><LogOut size={14} /> Sign Out</button>
        </div>
      </aside>

      <main className="main-content">
        {children}
      </main>

      {isAdmin && reportPopup && !reportDismissed && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#FFFFFF', borderRadius: 12, padding: 28, width: 'min(440px, 90vw)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.25)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <Calendar size={22} color="#6366F1" />
              <h3 style={{ margin: 0, fontSize: 18 }}>Monthly report ready</h3>
              <button onClick={() => setReportDismissed(true)}
                      style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={18} />
              </button>
            </div>
            <p style={{ color: '#374151', fontSize: 14, margin: '0 0 20px' }}>
              Last month's performance report{reportPopup.report_month ? ` (${monthLabel(reportPopup.report_month)})` : ''} is ready. Open it and click “Copy all” to pull the full team snapshot into Claude.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setReportDismissed(true)}>Cancel</button>
              <button className="btn btn-primary" onClick={() => { setReportDismissed(true); navigate('/admin/monthly-report'); }}>
                OK, take me to the report
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
